import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaChevronLeft } from 'react-icons/fa';
import './BlogPost.css';

const formatNumber = (value) => {
    if (typeof value !== 'number') {
        return '-';
    }

    return value.toLocaleString('en-US');
};

const ClashRoyaleInsights = ({ endpoint }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(Boolean(endpoint));
    const [error, setError] = useState('');

    useEffect(() => {
        if (!endpoint) {
            return;
        }

        let cancelled = false;

        const fetchStats = async () => {
            setLoading(true);
            setError('');

            try {
                const response = await fetch(endpoint);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || data.message || 'Unable to load Clash Royale stats');
                }

                if (!cancelled) {
                    setStats(data);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchStats();

        return () => {
            cancelled = true;
        };
    }, [endpoint]);

    if (loading) {
        return (
            <section className="clash-insights-panel">
                <div className="clash-loading">Loading live Clash Royale account stats...</div>
            </section>
        );
    }

    if (error || !stats) {
        return (
            <section className="clash-insights-panel">
                <div className="clash-error">
                    Live Clash Royale stats are unavailable right now. The server needs a valid
                    <code> CLASH_ROYALE_API_TOKEN </code>
                    configured.
                </div>
            </section>
        );
    }

    const coreStats = [
        ['Trophies', formatNumber(stats.trophies)],
        ['Best trophies', formatNumber(stats.bestTrophies)],
        ['Win rate', typeof stats.winRate === 'number' ? `${stats.winRate}%` : '-'],
        ['Battles', formatNumber(stats.battleCount)],
        ['Three-crowns', formatNumber(stats.threeCrownWins)],
        ['Avg. elixir', typeof stats.averageElixir === 'number' ? stats.averageElixir.toFixed(1) : '-']
    ];

    return (
        <section className="clash-insights-panel">
            <div className="clash-profile">
                <div>
                    <div className="clash-label">Live Account</div>
                    <h2>{stats.name}</h2>
                    <p>{stats.tag} • Level {stats.expLevel} • {stats.arena}</p>
                </div>
                <div className="clash-account-span">
                    <span>{stats.accountStart?.label || '2016 - Present'}</span>
                    <small>{stats.accountStart?.source}</small>
                </div>
            </div>

            <div className="clash-stat-grid">
                {coreStats.map(([label, value]) => (
                    <div className="clash-stat" key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                    </div>
                ))}
            </div>

            <div className="clash-context-row">
                <div>
                    <span>Clan</span>
                    <strong>{stats.clan || '-'}</strong>
                </div>
                <div>
                    <span>Favorite Card</span>
                    <strong>{stats.currentFavouriteCard?.name || '-'}</strong>
                </div>
            </div>

            {stats.currentDeck?.length > 0 && (
                <div className="clash-deck-section">
                    <h3>Current Deck</h3>
                    <div className="clash-deck-grid">
                        {stats.currentDeck.map((card) => (
                            <div className="clash-card" key={card.id || card.name}>
                                {card.iconUrl && <img src={card.iconUrl} alt={card.name} />}
                                <div>
                                    <strong>{card.name}</strong>
                                    <span>
                                        {typeof card.elixirCost === 'number' ? `${card.elixirCost} elixir` : 'Elixir n/a'} • Level {card.level}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

const BlogPost = () => {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/blog.json')
            .then(res => res.json())
            .then(data => {
                const found = data.find(p => p.id === id);
                setPost(found);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error loading blog post:", err);
                setLoading(false);
            });
    }, [id]);

    if (loading) return <div className="blog-post-container">Loading...</div>;

    if (!post) {
        return (
            <div className="blog-post-container">
                <Link to="/blog" className="back-link"><FaChevronLeft /> Back to Blog</Link>
                <h1>Post not found</h1>
            </div>
        );
    }

    return (
        <div className="blog-post-container">
            <div className="post-content-wrapper">
                <Link to="/blog" className="back-link"><FaChevronLeft /> Back to Blog</Link>

                <h1 className="post-hero-title">{post.title}</h1>
                <div className="post-meta">
                    {post.date} • {post.readTime} read
                </div>

                {post.type === 'clash-royale' && (
                    <ClashRoyaleInsights endpoint={post.statsEndpoint} />
                )}

                <div className="post-body" dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>
        </div>
    );
};

export default BlogPost;
